"use client";
import { useEffect, useMemo, useState } from "react";
import ReactSelect, {
  type GroupBase,
  type Props as ReactSelectProps,
} from "react-select";

/**
 * The option shape every select in the app builds: the value the form field
 * holds, plus the label shown in the menu.
 */
export interface SelectOption<TValue> {
  value: TValue;
  label: string;
}

/**
 * Typed counterpart to <Select />, which is declared `props: any` and so hands
 * every call site an untyped `onChange` argument.
 *
 * Generic over the option's value type, so `options`, `value` and `onChange`
 * agree: give it `SelectOption<number>[]` and the `onChange` argument is
 * `SelectOption<number> | null`, with no annotation needed at the call site.
 */
export type SelectV2Props<
  TValue,
  IsMulti extends boolean = false,
> = ReactSelectProps<
  SelectOption<TValue>,
  IsMulti,
  GroupBase<SelectOption<TValue>>
> & {
  /**
   * Rendered above the control.
   *
   * Deliberately no `error` / `errortext`: <Select /> accepts both and does
   * nothing with them (they are forwarded to react-select, which ignores
   * unknown props), so call sites that passed them were rendering their own
   * error text anyway. TextInput is where `errortext` actually does something.
   */
  label?: string;
  /**
   * Render the menu as a child of the control (the default) instead of a
   * fixed portal on <body>. iOS Safari misplaces fixed layers while the
   * on-screen keyboard is open, so a portalled menu ends up away from the
   * field it belongs to — it is positioned from getBoundingClientRect(),
   * which reports where the field should be, not where iOS drew it. A menu
   * inside the control is displaced along with it and stays attached.
   * Pass false only where an ancestor with clipped overflow would cut the
   * menu off, which is nowhere in the app today.
   */
  inlineMenu?: boolean;
};

export function SelectV2<TValue, IsMulti extends boolean = false>({
  inlineMenu = true,
  ...props
}: SelectV2Props<TValue, IsMulti>) {
  // Generate a stable instanceId based on the name prop or a fallback
  const instanceId = useMemo(() => {
    const baseId = props.name || props.id || "vellum-select";
    // Add a prefix to ensure uniqueness and avoid conflicts with other react-select instances
    return `vellum-${baseId}`;
  }, [props.name, props.id]);

  // Rendered into <body> so an open menu never contributes to the page's
  // layout or scroll height. Set after mount because document doesn't exist
  // during SSR; the menu is closed on first paint either way, so there is
  // nothing to mismatch on hydration.
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  useEffect(() => setPortalTarget(document.body), []);

  return (
    <div>
      {props.label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {props.label}
        </label>
      )}
      <ReactSelect<
        SelectOption<TValue>,
        IsMulti,
        GroupBase<SelectOption<TValue>>
      >
        instanceId={instanceId}
        isSearchable={props.isSearchable ?? true}
        // A portalled menu is "fixed" so it stays out of the scroll height of
        // whatever scrolls the page, which is what stops the page expanding.
        menuPortalTarget={inlineMenu ? null : portalTarget}
        menuPosition={inlineMenu ? "absolute" : "fixed"}
        // An inline menu flips above the control rather than growing the
        // scroll area when there isn't room for it below
        menuPlacement={inlineMenu ? "auto" : "bottom"}
        // Default is true: react-select scrolls the nearest scroll parent to
        // bring a menu opened near the bottom into view. That scroll is the
        // jump. Flipping above the control keeps the menu on screen instead.
        menuShouldScrollIntoView={false}
        // react-select mirrors the selected value into a hidden <input>, which
        // React rejects when the value is null (e.g. a "Not assigned" option).
        // Stringified because react-select's contract is a string and some
        // options hold numbers, such as the hour selects.
        getOptionValue={(option) => String(option.value ?? "")}
        components={{
          IndicatorSeparator: () => null,
        }}
        classNames={{
          // NOTE: all class names must be prefixed with "!" to override default styles
          control: () => {
            // 44px height comes from the default height of the <TextInput /> component
            return "rounded-md! text-sm! min-h-[44px]! text-black! h-fit! text-left!";
          },
          menu: () => {
            return "text-sm! z-50! text-left!";
          },
          multiValueLabel: () => {
            return "text-[12px]!";
          },
          multiValueRemove: () => {
            return "h-6! w-6! hover:bg-error! hover:text-white!";
          },
        }}
        theme={(theme) => ({
          ...theme,
          colors: {
            ...theme.colors,
            primary: "#286FCA", // Vellum blue
            primary25: "#F3F4F6",
          },
        })}
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          input: (base) => ({
            ...base,
            fontSize: "16px", // Prevent iOS zoom on focus (requires at least 16px)
          }),
        }}
        {...props}
        // react-select treats an undefined value as uncontrolled and keeps
        // showing its last pick, so a cleared field (options.find() missing)
        // has to arrive as null.
        value={props.value ?? null}
      />
    </div>
  );
}
